import { FC, useEffect, useRef } from "react";

import authService from "@http/services/auth.service";
import { useRedirectAfterLogin } from "@hooks/useRedirectAfterLogin";
import { useRouter } from "next/router";
import { getTenantConfig } from "src/utils/tenant";

function storedRedirectAfterLogin(): string | undefined {
  try {
    const raw = localStorage.getItem("redirect_after_login");
    if (!raw || raw === "undefined") {
      return undefined;
    }

    const parsed = JSON.parse(raw);
    return typeof parsed === "string" && parsed !== "" ? parsed : undefined;
  } catch {
    return undefined;
  }
}

/**
 * `?redirect=/tickets?entity=Ticket&customers=…` is parsed as
 * `redirect=/tickets?entity=Ticket` plus leftover keys. Re-attach those
 * keys so post-login navigation keeps the original filters.
 */
function redirectFromRouterQuery(
  query: Record<string, string | string[] | undefined>
): string | undefined {
  const redirect = query.redirect;
  if (typeof redirect !== "string" || redirect === "") {
    return undefined;
  }

  const leftoverKeys = Object.keys(query).filter((key) => key !== "redirect");
  if (leftoverKeys.length === 0) {
    return redirect;
  }

  const url = new URL(redirect, "http://local.invalid");
  leftoverKeys.forEach((key) => {
    const value = query[key];
    const values = Array.isArray(value) ? value : [value];
    values.forEach((entry) => {
      if (typeof entry === "string") {
        url.searchParams.append(key, entry);
      }
    });
  });

  return `${url.pathname}${url.search}${url.hash}`;
}

const Home: FC = () => {
  const router = useRouter();
  const [, setRedirect] = useRedirectAfterLogin();
  const redirectingRef = useRef(false);
  const bootstrappedRef = useRef(false);

  useEffect(() => {
    if (!router.isReady || bootstrappedRef.current) {
      return;
    }

    bootstrappedRef.current = true;

    const fromQuery = redirectFromRouterQuery(router.query);
    if (fromQuery) {
      setRedirect(fromQuery);
    }

    const desiredRedirect = fromQuery || storedRedirectAfterLogin();

    const bootstrapAuth = async () => {
      try {
        const user = await authService.fetchUser();
        if (user) {
          localStorage.removeItem("redirect_after_login");
          router.replace(
            desiredRedirect || process.env.NEXT_PUBLIC_DASHBOARD_URL || "/dashboard"
          );
          return;
        }
      } catch {
        // No valid user after fetchUser (and optional refresh) — redirect to login
      }

      if (!redirectingRef.current) {
        redirectingRef.current = true;
        const loginUrl = new URL(
          getTenantConfig().keycloakLoginUrl,
          window.location.origin
        );
        if (desiredRedirect) {
          loginUrl.searchParams.set("redirect", desiredRedirect);
        }
        window.location.href = loginUrl.toString();
      }
    };

    void bootstrapAuth();
  }, [router, setRedirect]);

  return null;
};

export default Home;
